/**
 * BulkUpload component - handles Excel template downloads and bulk product uploads
 */
import React, { useState, useRef } from 'react';
import { api } from '../../lib/store';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, BookOpen } from 'lucide-react';
import { logger } from '../../lib/logger';

export function BulkUpload() {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.bulkUpload(formData);
      setResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setResult({ error: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleExportAll = async () => {
    try {
      toast.info('Exporting your entire catalog...');
      const response = await api.exportAllProducts();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `HEDA_Product_Catalog_Export_${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Product catalog exported! Edit it in Excel and re-upload to bulk-update.');
    } catch (err) {
      logger.error('Export error:', err);
      toast.error(err.response?.data?.detail || 'Failed to export catalog');
    }
  };

  const handleDownloadManual = async () => {
    try {
      const res = await api.downloadProductUploadManual();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'HEDA_Plywoods_Product_Upload_Manual.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Product Upload Manual downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download manual');
    }
  };


  const handleDownloadTemplate = async (category = '') => {
    try {
      toast.info('Downloading template...');
      
      const response = await api.exportTemplate(category || undefined);
      
      // Check if backend returned HTML/JSON instead of xlsx (error case)
      const contentType = response.headers?.['content-type'] || '';
      if (!contentType.includes('spreadsheet') && !contentType.includes('xlsx')) {
        toast.error('Server did not return a valid Excel file');
        return;
      }

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const safeName = category ? category.replace(/[/\s]+/g, '_') : 'all';
      link.download = `HEDA_${safeName}_Template.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(downloadUrl);
      
      const pretty = category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Sample (All Categories)';
      toast.success(`${pretty} template downloaded!`);
    } catch (err) {
      logger.error('Download error:', err);
      toast.error(err.response?.data?.detail || 'Failed to download template. Please try again.');
    }
  };

  // Template button configurations — grouped by product type
  const templates = [
    { name: 'Plywood', key: 'plywood' },
    { name: 'Blockboard', key: 'blockboard' },
    { name: 'MDF', key: 'mdf' },
    { name: 'WPC/PVC', key: 'wpc/pvc' },
    { name: 'ACP', key: 'acp' },
    { name: 'Doors', key: 'doors' },
    { name: 'Laminates', key: 'laminates' },
    { name: 'Highlighters', key: 'highlighters' },
    { name: 'Veneers', key: 'veneers' },
    { name: 'Wooden Flooring', key: 'wooden-flooring' },
    { name: 'Adhesives', key: 'adhesives' },
    { name: 'Hardware', key: 'hardware' },
    { name: 'Screws / Patti', key: 'screws-patti' },
  ];

  return (
    <div className="max-w-2xl" data-testid="bulk-upload-section">
      <Card className="border-[#E2E6D9]">
        <CardContent className="pt-6 space-y-4">
          <div className="text-center">
            <FileSpreadsheet className="w-12 h-12 mx-auto text-[#556B2F] mb-3" />
            <h2 className="text-lg font-bold mb-1">Bulk Upload Products</h2>
            <p className="text-sm text-muted-foreground">Download category-specific templates and upload products</p>
            <Button
              onClick={handleDownloadManual}
              variant="outline"
              size="sm"
              className="mt-3 border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-white"
              data-testid="product-upload-manual-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Download Product Upload Manual (PDF)
            </Button>
          </div>

          {/* Template Downloads by Category */}
          <div className="p-4 bg-[#F2F4EF] rounded-lg border border-[#E2E6D9]">
            <p className="text-xs font-semibold text-[#556B2F] uppercase tracking-wide mb-3">Download Templates</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {templates.map((t) => (
                <Button 
                  key={t.key}
                  onClick={() => handleDownloadTemplate(t.key)} 
                  variant="outline" 
                  size="sm"
                  className="border-[#E2E6D9] hover:bg-[#F2F4EF] justify-start"
                  data-testid={`template-btn-${t.key}`}
                >
                  <Download className="w-3 h-3 mr-2 flex-shrink-0" /> {t.name}
                </Button>
              ))}
            </div>
            {/* Sample workbook */}
            <div className="mt-3 pt-3 border-t border-[#E2E6D9]">
              <Button
                onClick={() => handleDownloadTemplate('')}
                variant="outline"
                size="sm"
                className="w-full border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-white"
                data-testid="template-btn-sample"
              >
                <Download className="w-3 h-3 mr-1" /> Download Sample Workbook (all categories, pre-filled)
              </Button>
              <p className="text-[10px] text-[#5C6150] mt-1 text-center">
                Use this to see how every template looks with real data. Don't upload this directly — use the individual templates above.
              </p>
            </div>
          </div>

          {/* Template Info */}
          <div className="p-3 bg-[#FDF6E1] rounded-lg border border-[#DAA520]/30">
            <p className="text-xs font-semibold text-[#8B6914] uppercase tracking-wide mb-1">How templates work</p>
            <p className="text-xs text-[#5C6150]">
              Every template has two sheets: <strong>Data</strong> (with 2–3 example rows to copy) and <strong>Instructions</strong> (explains each column). Rate cells are rate per sqft for Plywood / Blockboard / MDF / WPC-PVC / ACP / Doors. Leave blank if a thickness isn't sold.
            </p>
          </div>

          {/* Export Current Catalog */}
          <div className="p-4 bg-[#556B2F]/10 rounded-lg border border-[#556B2F]/30">
            <p className="text-xs font-semibold text-[#556B2F] uppercase tracking-wide mb-2">Export current catalog</p>
            <Button
              onClick={handleExportAll}
              className="w-full bg-[#556B2F] hover:bg-[#465926] text-white"
              size="sm"
              data-testid="export-all-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download All Current Products (multi-sheet)
            </Button>
            <p className="text-[10px] text-[#5C6150] mt-2">
              Downloads every product in your database — one sheet per category (Plywood, Laminates, Doors, etc.). Edit rates/codes in Excel and re-upload the file below to bulk-update everything at once.
            </p>
          </div>
          
          {/* Update Info */}
          <div className="p-3 bg-[#E8F5E9] rounded-lg border border-green-200">
            <p className="text-xs font-semibold text-green-800 uppercase tracking-wide mb-1">Smart Update</p>
            <p className="text-xs text-[#5C6150]">
              All templates include main_category column. Re-uploading will update existing products instead of creating duplicates.
            </p>
          </div>

          {/* Upload Button */}
          <div className="flex gap-2">
            <Button 
              onClick={() => fileRef.current?.click()} 
              disabled={uploading} 
              className="w-full bg-[#556B2F] hover:bg-[#465926] text-white h-12" 
              data-testid="upload-excel-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload Excel File'}
            </Button>
          </div>

          <input 
            ref={fileRef} 
            type="file" 
            accept=".xlsx,.xls" 
            onChange={handleUpload} 
            className="hidden" 
            data-testid="file-input" 
          />

          {result && (
            <div 
              className={`p-3 rounded-lg text-sm ${result.error ? 'bg-red-50 text-[#B93838]' : 'bg-green-50 text-green-800'}`} 
              data-testid="upload-result"
            >
              {result.error || result.message}
              {(result.inserted !== undefined || result.updated !== undefined) && (
                <div className="mt-1 font-mono-data text-xs">
                  {result.inserted > 0 && <span className="text-green-700 font-bold">{result.inserted} new</span>}
                  {result.inserted > 0 && result.updated > 0 && <span>, </span>}
                  {result.updated > 0 && <span className="text-blue-700 font-bold">{result.updated} updated</span>}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
